import React, { useState, useEffect } from 'react';
import { Alert } from "@mui/lab";
import * as types from "../../../../constants";
import { ButtonAdd, ButtonBack, ButtonForward } from '../../../general/mini_components';
import { filterRecordsFromSection, filterRecordsFromSubmissions, numberRecordsSection } from '../../../../utils/index.jsx';
import { Translate, withLocalize } from 'react-localize-redux';
import PropTypes from 'prop-types';
import { EnhancedTable } from '../../../general/EnhancedTable';
import { Card, CardContent, Typography, Grid, Paper, IconButton } from '@mui/material';
import Loader from '../../../Loader';
import ShowSingleSubmissionPatient from '../../../../pages/hospital/ShowSingleSubmissionPatient';
import { RequestInfoWithFetch } from '../../../../pages/hospital/Request/RequestInfo';
import Modal from '../../../general/modal';
import { SubmissionPDFDocument } from '../../../../components/pdf/documents/SubmissionPDFDocument';
import { PDFDownloadButtonWithSignature } from '../../../../components/pdf/components/PDFDownloadButtonWithSignature';
import { usePDFConfig } from '../../../../hooks/usePDFConfig';
import { Link } from 'react-router-dom';
import { HOSPITAL_BILLING_EDIT } from '../../../../routes/urls';
import PrintIcon from '@mui/icons-material/Print';
import { useSelector } from 'react-redux';
import { extractPrescribingDoctor } from '../../../../utils/doctorUtils';
import { useResearcherRole } from '../../../../hooks/useResearcherRole';
import { extractSurveyNameSnapshot } from '../../../general/SmartFields/Templated/extractSurveyNameSnapshot';

/**
 * Component in charge of showing records of a given patient in a survey
 */
function ShowPatientRecords(props) {
    let [indexSubmission, setIndexSubmission ] = useState(-1);
    const [sectionSelected, setSectionSelected] = useState(null);
    const [showModalPrintPDF, setShowModalPrintPDF] = useState(false);
    const [submissionPrint, setSubmissionPrint] = useState(null);

    const [showError, setShowError] = useState(0);
    const { config: pdfConfig } = usePDFConfig();
    const rawLocale = props.activeLanguage?.code || 'en';
    
    // Normalize locale: ensure it's 'en', 'es', or 'fr'
    const locale = rawLocale?.toLowerCase().startsWith('en') ? 'en' : 
                   rawLocale?.toLowerCase().startsWith('es') ? 'es' : 'fr';
    
    // Debug: log the activeLanguage and locale
    console.log('[ShowPatientRecords] activeLanguage:', props.activeLanguage);
    console.log('[ShowPatientRecords] rawLocale:', rawLocale);
    console.log('[ShowPatientRecords] normalized locale:', locale);
    
    // Get investigation UUID for signature fetching
    const currentInvestigationUuid = useSelector((state) =>
        state.investigations.currentInvestigation?.uuid
    );
    // SHER-224: resolve the uploading researcher's role to label the PDF signature.
    const prescribingDoctorRole = useResearcherRole({
        uuidInvestigation: currentInvestigationUuid || props.uuidInvestigation || '',
        uuidResearcher: submissionPrint?.uuidResearcher,
        enabled: showModalPrintPDF,
    });

    function addRegistry(indexSection){
        setSectionSelected(indexSection);
    }

    function printSubmission(submission){
        setSubmissionPrint(submission);
        setShowModalPrintPDF(true);
    }

    function renderPDFModal(){
        if(!submissionPrint) return null;
        
        const currentSurvey = props.surveys?.find((survey) => survey.uuid === submissionPrint.uuidSurvey);
        if(!pdfConfig){
            return(
                <Modal key="modal" fullWidth medium open={showModalPrintPDF} title="" closeModal={() => setShowModalPrintPDF(false)}>
                    <Typography variant='body2'><Translate id="pages.hospital.patient.fill_billing_info" />, <Link to={HOSPITAL_BILLING_EDIT}><Translate id="general.here" /></Link></Typography>
                </Modal>
            )
        }
        else if(currentSurvey && props.patient){
            // Find prescribing doctor from the submission
            const prescribingDoctor = extractPrescribingDoctor(submissionPrint);
            // SHER-244 — use the snapshot for both the PDF title (via the
            // SubmissionPDFDocument's internal effectiveSurveyName) and for
            // the download filename so the user gets the original template
            // name even after a live rename.
            const snapshotName = extractSurveyNameSnapshot(
                submissionPrint?.surveyRecords || submissionPrint?.records,
            );
            const printNameSurvey = snapshotName ?? currentSurvey.name;

            return(
                <Modal key="modal" fullWidth medium open={showModalPrintPDF} title="" closeModal={() => setShowModalPrintPDF(false)}>
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                        <PDFDownloadButtonWithSignature
                            uuidInvestigation={currentInvestigationUuid || props.uuidInvestigation || ''}
                            uuidResearcher={prescribingDoctor?.uuid}
                            filename={`${printNameSurvey}-${submissionPrint.id}`}
                            variant="contained"
                            size="large"
                            label={<Translate id="general.download_pdf" />}
                        >
                            {(signatureBase64) => (
                                <SubmissionPDFDocument
                                    submission={submissionPrint}
                                    currentSurvey={currentSurvey}
                                    patient={props.patient}
                                    prescribingDoctor={prescribingDoctor}
                                    prescribingDoctorRole={prescribingDoctorRole || undefined}
                                    config={pdfConfig}
                                    nameSurvey={printNameSurvey}
                                    locale={locale}
                                    uuidInvestigation={currentInvestigationUuid || props.uuidInvestigation || ''}
                                    signatureBase64={signatureBase64}
                                />
                            )}
                        </PDFDownloadButtonWithSignature>
                    </div>
                </Modal>
            )
        }
        return null;
    }

 
    function renderSection(section, nRecords){
        let registers = "No hay registros";
        let addButton = <ButtonAdd onClick={() => addRegistry(section.uuid)} />;
        if(nRecords !== 0){
            registers = "Hay "+nRecords+" registros";
            if(!section.repeats){
                addButton = "No boton";
            }
        }
        return(
            { name : section.name, register : registers}
        )
    }
    
    function renderNavigation(){
        const dateCurrentSubmission = new Date(props.submissions[indexSubmission].updatedAt)
        return (
            <Grid item xs={12}>
                <Paper style={{padding:"1rem", marginTop:'1rem'}}>
                    <Grid item xs={12}>
                    <Typography variant="body2" gutterBottom>
                        <Translate id="hospital.data-collection" />:
                        {' '}
                        {/* SHER-244 — prefer the snapshot embedded in the
                            medical_history_template record's JSON value
                            (template_medical_history-survey_name) so renaming
                            the live template doesn't retroactively rewrite
                            this header. Falls back to the live name for older
                            submissions saved before the snapshot landed. */}
                        {
                            extractSurveyNameSnapshot(
                                props.submissions[indexSubmission].surveyRecords
                                    || props.submissions[indexSubmission].records
                            )
                            ?? props.submissions[indexSubmission].surveyName
                        }
                    </Typography>
                        <Typography variant="body2" gutterBottom><Translate id="hospital.doctor.label" />: {props.submissions[indexSubmission].researcher}</Typography>
                        <Typography variant="body2" gutterBottom><Translate id="general.date" />: {dateCurrentSubmission.toLocaleDateString()} {dateCurrentSubmission.toLocaleTimeString()}</Typography>                        
                    </Grid>
                    <Grid item>
                        <Typography variant="body2" gutterBottom>
                        {
                            `${indexSubmission+1} / ${props.submissions.length}`
                        }
                        </Typography>
                    </Grid>
                    <Grid item>
                        <ButtonBack disabled={indexSubmission === 0} onClick={() => setIndexSubmission(indexSubmission-1)}></ButtonBack>
                        <ButtonForward disabled={indexSubmission === props.submissions.length -1} onClick={() => setIndexSubmission(indexSubmission+1)}></ButtonForward>
                    </Grid>
                </Paper>
            </Grid>)
    }
    function renderCore(){
        if(showError === 0){
            if(!props.mode || props.mode === "table"){
                if(!sectionSelected){
                    const headCells = [{ id: "name", alignment: "right", label: <Translate id={`investigation.create.personal_data.fields.name`} /> }, 
                                         { id: "records", alignment: "right", label: <Translate id={`investigation.fill.records`} /> } 
                                        ];
                    const rows = props.survey.sections.map(section => {
                        const nRecords = numberRecordsSection(section, props.submissions);
                        return( 
                            renderSection(section, nRecords)
                        ) 
                    })
                    return(
                        <EnhancedTable titleTable="" rows={rows} headCells={headCells} 
                            actions={[{"type" : "add", "func" : (index) => addRegistry(index)}]}
                            />
                    )
                }
            }
            else{
                const submission = props.submissions[indexSubmission];
                const currentSurvey = props.surveys.find((sur) => sur.uuid === submission.uuidSurvey);
                const belongsToRequest = submission && types.TYPE_FILL_SURVEY.includes(submission.typeSurvey);
                return(
                <div style={{paddingTop:"0.6rem"}}>
                    {
                        belongsToRequest &&
                        <RequestInfoWithFetch key={submission.id} idSubmission={submission.id} uuidInvestigation={props.uuidInvestigation} />
                    }
                    <ShowSingleSubmissionPatient currentSurvey={currentSurvey} 
                        idSubmission={submission.id}
                        forceEdit={props.forceEdit} submission={submission} 
                        callBackEditSubmission={(uuidSubmission, uuidSection) => props.callBackEditSubmission(uuidSubmission, uuidSection)}
                        onPrintSubmission={() => printSubmission(submission)}  /> 
                </div> )
            }
        }
        else{
            return (
                <Alert mb={4} severity="error">
                    <Translate id="investigation.share.error.description" />
                </Alert>
            );
        }
    }
    useEffect(() => {
        const tempIndex = props.submissions.findIndex(sub => sub.id === props.idSubmission);
        setIndexSubmission(tempIndex);
    }, [props.idSubmission])
    
    if(indexSubmission === -1){
        return <Loader />
    }
    return (
        <Grid container direction="column" spacing={2}>
            {renderPDFModal()}
            {
                sectionSelected !== null && 
                <Grid item xs={12}>
                    <ButtonBack onClick={() => setSectionSelected(null)} >Back</ButtonBack>
                </Grid>
            }
            {
                props.singlePatient &&
                <Grid item xs={12}>
                    <Typography variant="subtitle1">
                        <Translate id="investigation.fill.survey.patient_name" />: {`${props.patientPersonalData.name} ${props.patientPersonalData.surnames}`} - {props.patientPersonalData.uuid}
                    </Typography>
                </Grid>
            }
            {
                props.submissions.length > 1 && !props.singleSubmission &&
                renderNavigation()
            }
            <Grid item xs={12}>
                {
                    renderCore()
                }
            </Grid>
        </Grid>
    )
}

ShowPatientRecords.propTypes = {
    /**
     Survey where the data was stored
    */
    survey:PropTypes.object,
    /**
     All Submissions Patient
    */
     submissions: PropTypes.array,
     permissions: PropTypes.array,
     callBackEditSubmission: PropTypes.func,
     idSubmission: PropTypes.number,
     surveys:PropTypes.array,
     forceEdit:PropTypes.bool,
     patient: PropTypes.object,
     activeLanguage: PropTypes.object,
    /**
     Mostrar datos en modo tabla o modo elementos
    */
    mode : PropTypes.string
};

export default withLocalize(ShowPatientRecords);
